import type {
  AwardTypeCode,
  ChargeCodeSpend,
  Me,
  PoolPosition,
  Spend,
  SpendQuery,
} from '@myrewards/contracts';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { Link } from 'react-aria-components';
import { meQuery } from '../api';
import {
  registerExportHref,
  registerQuery,
  spendQuery,
  spendSearch,
  spendView,
  type Loose,
} from '../api/insights';
import { Hero, initialsOf, PageBody, type HeroStat } from '../shell';
import {
  Card,
  cx,
  EmptyState,
  formatCount,
  formatMoney,
  formatMonth,
  plural,
  scoped,
  Select,
  tableCards,
  useCardRows,
  type DataColumn,
  HelpShownTag,
  useHelpAnchor,
} from '../ui';
import { fiscalYearOn } from '../ui/fiscal';
import { canOpenArea } from '../settings/nav';
import {
  BarRows,
  barTicks,
  ChartFigure,
  ChartTable,
  Columns,
  GhostOverlay,
  Legend,
  niceMax,
  ViewToggle,
} from './charts';
import {
  ExportLink,
  InsightsTabs,
  KpiTile,
  ScrollArea,
  Segmented,
  useLibrary,
  usePreviewing,
  useView,
} from './frame';
import {
  budgetSetting,
  canOpen,
  monthlyShare,
  scopeName,
  scopeWords,
  sectorsOnly,
  shortMoney,
  yearOption,
  yearSpan,
} from './labels';
import s from './Insights.module.css';
import sp from './Spend.module.css';

const TYPE_GROUPS: Record<
  Spend['byType'][number]['group'],
  { label: string; types: AwardTypeCode[] }
> = {
  discretionary: { label: 'Discretionary (Sector and Executive)', types: ['SDB', 'EDB'] },
  paa: { label: 'PAA', types: ['PAA'] },
  taa: { label: 'TAA', types: ['TAA'] },
};

/** The register's stages for a Spend status (paid, in progress, or both; never a returned draft). */
/**
 * Spend counts each person's line where it is (Payroll pays person by person), so its export asks
 * All awards for the same lines: paid, in progress, or either (APP-045 A28). Never by the award's
 * stage, which can sit on the other side of the line while some of its people are paid.
 */
const LINE_STATUS_OF: Record<SpendQuery['status'], 'sent' | 'paid' | 'in_progress'> = {
  all: 'sent',
  paid: 'paid',
  in_progress: 'in_progress',
};

/** A concise label for the exact filtered slice, without changing the reader's report scope. */
function spendViewLabel(view: SpendQuery, spend: Spend, fallback: string): string {
  const labels: string[] = [];
  if (view.poolId !== undefined) {
    labels.push(
      spend.budgetVsActual.find((position) => position.pool.id === view.poolId)?.pool.name ??
        'Selected pool',
    );
  }
  if (view.type !== undefined) {
    const types = new Set(view.type);
    if (types.size === 2 && types.has('SDB') && types.has('EDB')) labels.push('Discretionary');
    else if (view.type.length === 1 && view.type[0] === 'PAA') labels.push('PAA');
    else if (view.type.length === 1 && view.type[0] === 'TAA') labels.push('TAA');
    else labels.push('Selected award types');
  }
  if (view.status !== 'all') labels.push(view.status === 'paid' ? 'Paid' : 'In progress');
  if (view.costType !== undefined) labels.push(view.costType === 'OH' ? 'OH' : 'G&A');
  if (labels.length === 0) return fallback;
  return labels.length === 1 ? (labels[0] ?? fallback) : 'This view';
}

function BudgetCard({
  spend,
  awardsHref,
  setBudget,
}: {
  spend: Spend;
  awardsHref: (extra: Record<string, string>, preserveView?: boolean) => string | null;
  /** Where this person sets a budget; null when they only read budgets. */
  setBudget: { href: string; where: string } | null;
}): ReactElement {
  const [asTable, setAsTable] = useState(false);
  const pools = spend.budgetVsActual;
  const max = niceMax(
    Math.max(
      0,
      ...pools.flatMap((p) => [
        p.budget?.cashCents ?? 0,
        p.paid.cashCents + p.inProgress.cashCents,
      ]),
    ),
  );
  const columns: DataColumn<PoolPosition>[] = [
    { id: 'pool', header: 'Pool', cell: (p) => p.pool.name },
    {
      id: 'budget',
      header: 'Budget',
      cell: (p) => (p.budget === null ? 'Not set' : formatMoney(p.budget.cashCents)),
      numeric: true,
    },
    { id: 'paid', header: 'Paid', cell: (p) => formatMoney(p.paid.cashCents), numeric: true },
    {
      id: 'progress',
      header: 'In progress',
      cell: (p) => formatMoney(p.inProgress.cashCents),
      numeric: true,
    },
    {
      id: 'left',
      header: 'Remaining',
      cell: (p) =>
        p.remaining === null
          ? 'Not set'
          : p.remaining.cashCents < 0
            ? `${formatMoney(-p.remaining.cashCents)} over budget`
            : formatMoney(p.remaining.cashCents),
      numeric: true,
    },
  ];
  return (
    <Card
      title="Budget vs actual · by pool"
      help={{
        shows: `For each pool: paid, in progress and what’s left against its ${String(spend.fiscalYear)} budget, all on one scale, as Budget pools counts them: every award charged to the pool, Spot awards included. The filters above don’t change it. The other cards count the awards All awards lists.`,
        canDo: 'Open a pool to see the awards behind it, or switch to the table.',
      }}
      aside={<ViewToggle asTable={asTable} onChange={setAsTable} />}
    >
      <p className={s.cardMeta}>
        Every award charged to each pool, Spot awards included. The other cards count the awards All
        awards lists.
      </p>
      {asTable ? (
        <ChartTable
          caption={`Budget vs actual · FY ${String(spend.fiscalYear)}`}
          columns={columns}
          rows={pools}
          rowKey={(p) => p.pool.id}
          empty={{
            title: 'No pools in this view.',
            body: 'Pools show here once you can see them.',
          }}
        />
      ) : pools.length === 0 ? (
        <EmptyState
          variant="first-day"
          art="pool"
          size="sm"
          headline="No pools in this view"
          body="Each pool shows here with what’s paid, in progress and left against its budget."
        />
      ) : (
        <>
          <Legend
            items={[
              { key: 'paid', label: 'Paid', tone: 'petrol' },
              { key: 'progress', label: 'In progress', tone: 'cyan' },
              { key: 'left', label: 'Budget', tone: 'track' },
            ]}
          />
          <ChartFigure
            readable
            // The pools' own figures (Spot awards included), not the cards' (A48).
            summary={`${formatMoney(pools.reduce((n, p) => n + p.paid.cashCents, 0))} paid and ${formatMoney(pools.reduce((n, p) => n + p.inProgress.cashCents, 0))} in progress across ${plural(pools.length, 'pool', 'pools')}.`}
          >
            <BarRows
              max={max}
              ticks={barTicks(max, shortMoney)}
              rows={pools.map((p) => {
                const href = awardsHref({ poolId: p.pool.id }, false);
                return {
                  key: p.pool.id,
                  label:
                    href === null ? (
                      p.pool.name
                    ) : (
                      <Link href={href} className={s.link}>
                        {p.pool.name}
                      </Link>
                    ),
                  bars: [
                    {
                      track: p.budget?.cashCents ?? null,
                      segments: [
                        { value: p.paid.cashCents, tone: 'petrol' },
                        { value: p.inProgress.cashCents, tone: 'cyan' },
                      ],
                    },
                  ],
                  value:
                    p.budget === null ? (
                      <>
                        <span
                          className={s.strong}
                        >{`${formatMoney(p.paid.cashCents + p.inProgress.cashCents)} awarded`}</span>
                        <span className={s.badge}>Budget not set</span>
                        {setBudget !== null && (
                          <Link
                            href={setBudget.href}
                            className={s.link}
                            aria-label={`Set the budget for ${p.pool.name} in ${setBudget.where}`}
                          >
                            Set the budget
                          </Link>
                        )}
                      </>
                    ) : (
                      <>
                        <span
                          className={s.strong}
                        >{`${formatMoney(p.paid.cashCents)} paid of ${formatMoney(p.budget.cashCents)}`}</span>
                        <span className={s.cellSub}>
                          {`${formatMoney(p.inProgress.cashCents)} in progress · `}
                          {(p.remaining?.cashCents ?? 0) < 0
                            ? `${formatMoney(-(p.remaining?.cashCents ?? 0))} over budget`
                            : `${formatMoney(p.remaining?.cashCents ?? 0)} left`}
                        </span>
                        {(p.remaining?.cashCents ?? 0) < 0 && (
                          <span className={s.badgeProblem}>Over budget</span>
                        )}
                      </>
                    ),
                };
              })}
            />
          </ChartFigure>
        </>
      )}
      <p className={s.note}>
        One shared scale for every pool. Remaining = budget − paid − in progress.
      </p>
    </Card>
  );
}

function MonthCard({ spend }: { spend: Spend }): ReactElement {
  const [asTable, setAsTable] = useState(false);
  const total = spend.byMonth.reduce((sum, m) => sum + m.paidCashCents, 0);
  type Month = Spend['byMonth'][number];
  const data = spend.byMonth.map((m) => ({
    key: m.month,
    label: formatMonth(m.month),
    value: m.paidCashCents,
    display: shortMoney(m.paidCashCents),
  }));
  const columns: DataColumn<Month>[] = [
    { id: 'month', header: 'Month', cell: (m) => `${formatMonth(m.month)} ${m.month.slice(0, 4)}` },
    { id: 'paid', header: 'Cash paid', cell: (m) => formatMoney(m.paidCashCents), numeric: true },
  ];
  const peak = spend.byMonth.reduce<Month | null>(
    (best, m) => (best === null || m.paidCashCents > best.paidCashCents ? m : best),
    null,
  );
  return (
    <Card
      title="Cash paid · by month"
      help={{
        shows: 'Gross cash paid each month this year, by the month Payroll paid it.',
        canDo: 'Switch to the table for each month, or export the award-level detail.',
      }}
      aside={<ViewToggle asTable={asTable} onChange={setAsTable} />}
    >
      <p className={s.cardMeta}>
        {total === 0 ? '$0 so far · nothing paid yet' : `${formatMoney(total)} so far`}
      </p>
      {asTable ? (
        <ChartTable
          caption={`Cash paid by month · ${String(spend.fiscalYear)}`}
          columns={columns}
          rows={spend.byMonth}
          rowKey={(m) => m.month}
          empty={{ title: 'No months yet.', body: 'Each month shows here as the year goes on.' }}
        />
      ) : total === 0 ? (
        <GhostOverlay
          headline="Your first payout will start this chart"
          body="Cash paid each month, from the earning statements Payroll attaches to every cash award."
        >
          <Columns
            data={data}
            tick={shortMoney}
            ghost
            // An even month's share of the pools' budgets: the scale the first payouts will use.
            ghostMax={monthlyShare(
              spend.budgetVsActual.reduce((sum, p) => sum + (p.budget?.cashCents ?? 0), 0),
            )}
          />
        </GhostOverlay>
      ) : (
        <ChartFigure
          summary={`${formatMoney(total)} paid in ${String(spend.fiscalYear)}${peak !== null ? `; the most in ${formatMonth(peak.month)}, ${formatMoney(peak.paidCashCents)}` : ''}.`}
        >
          <Columns data={data} tick={shortMoney} tone="petrol" />
        </ChartFigure>
      )}
    </Card>
  );
}

function ChargeCodeCard({
  spend,
  codesHref,
}: {
  spend: Spend;
  /** Settings › Charge codes, for people who can open it. */
  codesHref: string | null;
}): ReactElement {
  const rows = spend.byChargeCode;
  const paid = rows.reduce((sum, r) => sum + r.paidCashCents, 0);
  const progress = rows.reduce((sum, r) => sum + r.inProgressCashCents, 0);
  const caption = `By charge code · FY ${String(spend.fiscalYear)}`;
  const c = tableCards(useCardRows());
  return (
    <Card
      title="By charge code"
      help={{
        shows:
          'Paid and in-progress cash by accounting project, account and org, the way it lands in the general ledger.',
        canDo:
          'Narrow to OH or G&A with the cost type above, or export one row per person for the ledger.',
      }}
      aside={
        <span className={s.cardMeta}>
          {rows.length === 0
            ? 'OH and G&A · nothing charged yet'
            : `OH and G&A · ${plural(rows.length, 'charge code', 'charge codes')}`}
        </span>
      }
    >
      {/* Below 720 px each charge code is a card, Paid and In progress in reach, rather than a
          table that scrolls sideways (FINANCE-HOME at 390, DESIGN-SPEC §9). */}
      <ScrollArea label={caption}>
        <table {...c.table(s.table)}>
          <caption>{caption}</caption>
          <thead {...c.head}>
            <tr {...c.row}>
              <th scope="col" {...c.header}>
                Project
              </th>
              <th scope="col" {...c.header}>
                Account
              </th>
              <th scope="col" {...c.header}>
                Org
              </th>
              <th scope="col" {...c.header}>
                Cost type
              </th>
              <th scope="col" className={s.num} {...c.header}>
                Paid
              </th>
              <th scope="col" className={s.num} {...c.header}>
                In progress
              </th>
            </tr>
          </thead>
          <tbody {...c.body}>
            {rows.length === 0 ? (
              <tr {...c.row}>
                <td colSpan={6} className={s.emptyCell} {...c.wide}>
                  <strong>Nothing charged yet.</strong> Each project and account set in{' '}
                  {codesHref === null ? (
                    'Charge codes'
                  ) : (
                    <Link href={codesHref} className={s.link}>
                      Charge codes
                    </Link>
                  )}{' '}
                  shows here once an award on it is approved.
                </td>
              </tr>
            ) : (
              rows.map((r: ChargeCodeSpend) => (
                <tr
                  key={`${r.costType}:${r.account}:${r.project ?? 'home'}:${r.org ?? 'home'}`}
                  {...c.row}
                >
                  <th scope="row" {...c.cell('Project', 'title')}>
                    <span className={cx(s.strong, r.project !== null && sp.code)}>
                      {r.project ?? 'No project'}
                    </span>
                    <span className={s.cellSub}>
                      {r.project === null ? 'PAA/TAA · account and home org' : (r.pool?.name ?? '')}
                    </span>
                  </th>
                  <td {...c.cell('Account', 'value', s.nowrap)}>
                    <span className={sp.code}>{r.account}</span>
                  </td>
                  <td {...c.cell('Org')}>
                    {r.homeOrg || r.org === null ? (
                      'Employee’s home org'
                    ) : (
                      <span className={sp.code}>{r.org}</span>
                    )}
                  </td>
                  <td {...c.cell('Cost type')}>{r.costType === 'GA' ? 'G&A' : 'OH'}</td>
                  <td {...c.cell('Paid', 'value', s.num)}>
                    <span className={s.strong}>{formatMoney(r.paidCashCents)}</span>
                  </td>
                  <td {...c.cell('In progress', 'value', s.num)}>
                    {formatMoney(r.inProgressCashCents)}
                  </td>
                </tr>
              ))
            )}
            {c.on && rows.length > 0 && (
              <tr {...c.row}>
                <th scope="row" {...c.cell('Total', 'title')}>
                  Total
                </th>
                <td {...c.cell('Paid', 'value', s.num)}>{formatMoney(paid)}</td>
                <td {...c.cell('In progress', 'value', s.num)}>{formatMoney(progress)}</td>
              </tr>
            )}
          </tbody>
          {!c.on && (
            <tfoot>
              <tr className={s.totalRow}>
                <th scope="row" colSpan={4}>
                  Total
                </th>
                <td className={s.num}>{formatMoney(paid)}</td>
                <td className={s.num}>{formatMoney(progress)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </ScrollArea>
      <p className={s.note}>
        Listed by project number. PAA/TAA lines carry no project, so they come last: the account
        plus the employee’s home org. Project numbers are shown exactly as they’re coded.
      </p>
    </Card>
  );
}

function TypeCard({
  spend,
  awardsHref,
}: {
  spend: Spend;
  awardsHref: (extra: Record<string, string>) => string | null;
}): ReactElement {
  const max = Math.max(1, ...spend.byType.map((t) => t.cashCents));
  const nothing = spend.byType.every((t) => t.awards === 0);
  return (
    <Card
      title="By award type"
      titleSize="sm"
      compact
      help={{
        shows: 'Cash awarded this year, by award type.',
        canDo: 'Open a type to see its awards.',
      }}
      aside={<span className={s.cardMeta}>Cash awarded</span>}
    >
      {nothing ? (
        <EmptyState
          variant="first-day"
          art="chart"
          size="sm"
          headline="No awards yet"
          body="Cash awarded by type, from the first approved award on."
        />
      ) : (
        <BarRows
          stacked
          compact
          max={max}
          rows={spend.byType.map((t) => {
            const href = awardsHref({ type: TYPE_GROUPS[t.group].types.join(',') });
            return {
              key: t.group,
              label:
                href === null ? (
                  TYPE_GROUPS[t.group].label
                ) : (
                  <Link href={href} className={s.link}>
                    {TYPE_GROUPS[t.group].label}
                  </Link>
                ),
              sub: plural(t.awards, 'award', 'awards'),
              bars: [{ segments: [{ value: t.cashCents, tone: 'navy' }] }],
              value: <span className={s.strong}>{formatMoney(t.cashCents)}</span>,
            };
          })}
        />
      )}
    </Card>
  );
}

function GranterCard({
  spend,
  awardsHref,
}: {
  spend: Spend;
  awardsHref: (extra: Record<string, string>) => string | null;
}): ReactElement {
  const max = Math.max(1, ...spend.byGranter.map((g) => g.cashCents));
  return (
    <Card
      title="By granter"
      titleSize="sm"
      compact
      help={{
        shows:
          'Who granted the awards, paid and in progress. Delegate entries count under their Sector Lead.',
        canDo: 'Open a name to see the awards they granted.',
      }}
      aside={<span className={s.cardMeta}>Paid + in progress</span>}
    >
      {spend.byGranter.length === 0 ? (
        <EmptyState
          variant="first-day"
          art="people"
          size="sm"
          headline="No one has granted an award yet"
          body="Each Sector Lead and the Executives appear here as they grant, delegates included."
        />
      ) : (
        <BarRows
          stacked
          compact
          max={max}
          rows={spend.byGranter.map((g) => {
            const href = awardsHref({ enteredById: g.person.id });
            return {
              key: g.person.id,
              label:
                href === null ? (
                  g.person.name
                ) : (
                  <Link href={href} className={s.link}>
                    {g.person.name}
                  </Link>
                ),
              sub: [
                g.sector?.name ?? null,
                g.delegates.length > 0
                  ? `incl. ${g.delegates.map((d) => d.name).join(' and ')}`
                  : null,
              ]
                .filter((x) => x !== null)
                .join(' · '),
              bars: [{ segments: [{ value: g.cashCents, tone: 'navy' }] }],
              value: (
                <>
                  <span className={s.strong}>{formatMoney(g.cashCents)}</span>
                  <span className={s.cellSub}>{plural(g.awards, 'award', 'awards')}</span>
                </>
              ),
            };
          })}
        />
      )}
    </Card>
  );
}

function RecipientsCard({
  spend,
  awardsHref,
}: {
  spend: Spend;
  awardsHref: (extra: Record<string, string>) => string | null;
}): ReactElement {
  return (
    <Card
      title="Top recipients"
      titleSize="sm"
      compact
      help={{
        shows: 'The people who received the most cash this year, with how many awards.',
        canDo:
          'Open an amount to see the awards. Handy for spotting where recognition concentrates.',
      }}
      aside={<span className={s.cardMeta}>{`${String(spend.fiscalYear)} · cash`}</span>}
    >
      {spend.topRecipients.length === 0 ? (
        <EmptyState
          variant="first-day"
          art="award"
          size="sm"
          headline="No recipients yet"
          body="The people recognized most this year will show here, with how many awards and how much cash."
        />
      ) : (
        <ul className={s.list}>
          {spend.topRecipients.map((r) => {
            const href = awardsHref({ personId: r.person.id });
            return (
              <li key={r.person.id} className={s.listItem}>
                <span className={s.avatar} aria-hidden="true">
                  {initialsOf(r.person.name)}
                </span>
                <span className={s.who}>
                  <span className={s.whoName}>{r.person.name}</span>
                  <span className={s.whoMeta}>
                    {[r.sector?.name ?? null, plural(r.awards, 'award', 'awards')]
                      .filter((x) => x !== null)
                      .join(' · ')}
                  </span>
                </span>
                <span className={s.amount}>
                  {href === null ? (
                    <span className={s.amountValue}>{formatMoney(r.cashCents)}</span>
                  ) : (
                    <Link
                      href={href}
                      className={s.link}
                      aria-label={`${formatMoney(r.cashCents)}: open ${r.person.name}’s awards`}
                    >
                      {formatMoney(r.cashCents)}
                    </Link>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

/** F1: what's been paid, what's in progress and what's left, by pool, type, charge code and person. */
/**
 * Spend's body: the filters, the KPI tiles and the cards (F1). The Spend page draws it under its
 * hero and tabs, and Finance's Home under Waiting on you (APP-054); the filters live in the
 * address either way, so a link to Spend keeps them.
 */
export function SpendDashboard({
  spend,
  view,
  setView,
}: {
  spend: Spend;
  view: SpendQuery;
  setView: (patch: Loose<SpendQuery>) => void;
}): ReactElement {
  const library = useLibrary();
  const previewing = usePreviewing();
  const { data: me } = useSuspenseQuery(meQuery);
  const thisYear = fiscalYearOn(spend.asOf);
  const year = spend.fiscalYear;
  const name = spend.scope.kind === 'company' ? 'All pools' : scopeName(spend.scope);
  const k = spend.kpis;
  // Options still to grant this year, from the pools' own options budgets (the pool filter
  // narrows it to that pool; the others don't apply to pools).
  const optionPools = spend.budgetVsActual.filter(
    (p) => view.poolId === undefined || p.pool.id === view.poolId,
  );
  const optionsBudgeted = optionPools.some((p) => (p.budget?.options ?? 0) > 0);
  const optionsLeft = optionPools.reduce(
    (sum, p) => sum + Math.max(0, p.remaining?.options ?? 0),
    0,
  );
  const registerView = {
    period: 'fiscal_year' as const,
    fiscalYear: year === thisYear ? undefined : year,
    poolId: view.poolId,
    type: view.type,
    costType: view.costType,
    lineStatus: LINE_STATUS_OF[view.status],
  };
  // The by-person export holds one row per matching person; the register counts them.
  const { data: register } = useQuery(registerQuery({ ...registerView, size: 1 }));
  const rows =
    register === undefined ? null : (register.matched?.people ?? register.totals.recipients);
  const awardsHref = (extra: Record<string, string>, preserveView = true): string | null => {
    if (!canOpen(library, 'all_awards')) return null;
    const params = new URLSearchParams();
    if (year !== thisYear) params.set('fiscalYear', String(year));
    if (preserveView) {
      if (view.poolId !== undefined) params.set('poolId', view.poolId);
      if (view.type !== undefined) params.set('type', view.type.join(','));
      if (view.costType !== undefined) params.set('costType', view.costType);
      params.set('lineStatus', LINE_STATUS_OF[view.status]);
    }
    for (const [key, value] of Object.entries(extra)) params.set(key, value);
    return `/insights/awards?${params.toString()}`;
  };
  const typeValue = view.type === undefined ? 'all' : view.type.join(',');
  // The report's filters, for Help's "Show me" (APP-090 §8).
  const filters = useHelpAnchor('insights-filters');
  return (
    <div className={s.stack}>
      <section className={s.filterBar} aria-label="This page shows" {...filters.props}>
        <HelpShownTag shown={filters.shown} placement="edge" />
        <div className={s.filterRow}>
          <Select
            label="Year"
            value={String(year)}
            options={[thisYear, thisYear - 1, thisYear - 2].map((y) => ({
              id: String(y),
              label: yearOption(y, spend.asOf),
            }))}
            onChange={(id) => {
              if (id !== null)
                setView({ fiscalYear: Number(id) === thisYear ? undefined : Number(id) });
            }}
          />
          <Select
            label="Pool"
            value={view.poolId ?? 'all'}
            options={[
              { id: 'all', label: 'All pools' },
              ...spend.budgetVsActual.map((p) => ({ id: p.pool.id, label: p.pool.name })),
            ]}
            onChange={(id) => {
              setView({ poolId: id === null || id === 'all' ? undefined : id });
            }}
          />
          <Select
            label="Award type"
            value={typeValue}
            options={[
              { id: 'all', label: 'All award types' },
              { id: 'SDB,EDB', label: 'Discretionary' },
              { id: 'PAA', label: 'PAA' },
              { id: 'TAA', label: 'TAA' },
            ]}
            onChange={(id) => {
              setView({
                type: id === null || id === 'all' ? undefined : (id.split(',') as AwardTypeCode[]),
              });
            }}
          />
          <Select
            label="Status"
            value={view.status}
            options={[
              { id: 'all', label: 'Paid and in progress' },
              { id: 'paid', label: 'Paid' },
              { id: 'in_progress', label: 'In progress' },
            ]}
            onChange={(id) => {
              if (id !== null) setView({ status: id as SpendQuery['status'] });
            }}
          />
          <Segmented
            label="Cost type"
            value={view.costType ?? 'all'}
            options={[
              { id: 'all', label: 'All' },
              { id: 'OH', label: 'OH' },
              { id: 'GA', label: 'G&A' },
            ]}
            onChange={(next) => {
              setView({ costType: next === 'all' ? undefined : next });
            }}
          />
          {canOpen(library, 'all_awards') && (
            <ExportLink
              href={registerExportHref(registerView, 'line')}
              label="Export to Excel"
              rows={
                rows === null
                  ? 'One row per person'
                  : `${plural(rows, 'row', 'rows')}, one per person`
              }
              previewing={previewing}
              empty={
                rows === 0
                  ? 'Nothing to export yet. Each person on an approved award adds a row.'
                  : null
              }
            />
          )}
        </div>
        <p className={s.filterNote}>
          {spend.filtersOn
            ? 'Filters are on: everything below except Budget vs actual follows them. '
            : ''}
          Every number drills down to the awards behind it.
        </p>
      </section>
      <div className={s.kpis}>
        <KpiTile
          label={scoped(spendViewLabel(view, spend, name), `options granted ${String(year)}`)}
          value={formatCount(k.options.granted)}
          note={
            k.options.granted === 0
              ? optionsBudgeted
                ? `${formatCount(optionsLeft)} available`
                : 'None granted yet · no options budget set'
              : `${formatMoney(Math.round(k.options.valueCents / 100) * 100)} at the FMV each award used`
          }
          help={{
            shows: `Options on the awards sent this year, and what they’re worth at the FMV each award was calculated with.`,
            canDo:
              'Open Budgets & history for the options budget, or export the awards for each grant.',
          }}
        />
        <KpiTile
          label={scoped(
            spendViewLabel(view, spend, 'All award types'),
            `average award ${String(year)}`,
          )}
          value={
            k.average.awards === 0 || k.average.cashCents === null
              ? plural(k.average.awards, 'award', 'awards')
              : formatMoney(Math.round(k.average.cashCents / 100) * 100)
          }
          note={
            k.average.awards === 0
              ? 'The first award sets the average'
              : `${plural(k.average.awards, 'award', 'awards')} · ${plural(k.average.people, 'person', 'people')}`
          }
          help={{
            shows:
              view.type === undefined
                ? 'The average cash per award this year, across every award type, with how many awards and people it covers.'
                : 'The average cash per award in this view, with how many awards and people it covers.',
            canDo:
              'Narrow by pool, award type or cost type above to see the average for just those.',
          }}
        />
      </div>
      <div className={s.split}>
        <div className={s.stack}>
          <BudgetCard spend={spend} awardsHref={awardsHref} setBudget={budgetSetting(me)} />
          <MonthCard spend={spend} />
          <ChargeCodeCard
            spend={spend}
            codesHref={canOpenArea(me, 'charge-codes') ? '/settings/charge-codes' : null}
          />
        </div>
        <div className={s.stack}>
          <TypeCard spend={spend} awardsHref={awardsHref} />
          <GranterCard spend={spend} awardsHref={awardsHref} />
          <RecipientsCard spend={spend} awardsHref={awardsHref} />
        </div>
      </div>
    </div>
  );
}

/** Spend's hero words and numbers, scoped and dated; Finance's Home shows the same (F1). */
export function spendHero(
  spend: Spend,
  view: SpendQuery,
  me: Pick<Me, 'roles'>,
): { sub: string; stats: HeroStat[] } {
  const year = spend.fiscalYear;
  const name = spendViewLabel(
    view,
    spend,
    spend.scope.kind === 'company' ? 'All pools' : scopeName(spend.scope),
  );
  const k = spend.kpis;
  const span = yearSpan(year, spend.asOf);
  const setBudget = budgetSetting(me);
  const budgetLabel = scoped(
    k.budgetLeft === null ? 'Set pools' : plural(k.budgetLeft.pools, 'set pool', 'set pools'),
    `budget left ${String(year)}`,
  );
  // Nothing charged to a pool yet (Spot awards included): what's left is the whole budget.
  const fullBudget = spend.budgetVsActual.reduce((sum, p) => sum + (p.budget?.cashCents ?? 0), 0);
  const budgetStat: HeroStat =
    k.budgetLeft !== null
      ? {
          label: budgetLabel,
          value: formatMoney(k.budgetLeft.cashCents),
          // A48: from Budget vs actual, which counts every award charged to a pool, Spot included.
          meta:
            k.budgetLeft.cashCents === fullBudget
              ? `The full budget · ${plural(k.budgetLeft.pools, 'pool', 'pools')}`
              : 'As Budget pools counts it',
        }
      : setBudget !== null
        ? {
            label: budgetLabel,
            value: 'Not set',
            notSet: {
              message: `Set it in ${setBudget.where}`,
              actionLabel: 'Set the budgets',
              href: setBudget.href,
            },
          }
        : { label: budgetLabel, value: 'Not set', meta: 'No budget is set yet', tone: 'muted' };
  return {
    sub:
      k.paidCashCents === 0 && k.inProgressCashCents === 0
        ? `The ${String(year)} budgets are in and nothing has been spent yet. As awards are approved and paid, this page fills in by pool, award type, charge code, granter and recipient.`
        : 'What we’ve paid, what’s in progress and what’s left, by pool, award type, charge code, granter and recipient.',
    stats: [
      {
        label: scoped(name, `cash paid ${String(year)}`),
        value: formatMoney(k.paidCashCents),
        meta: k.paidCashCents === 0 ? `Nothing paid yet in ${String(year)}` : span,
      },
      {
        label: scoped(name, 'in progress'),
        value: formatMoney(k.inProgressCashCents),
        meta:
          k.inProgressCashCents > 0
            ? 'Sent, not paid yet'
            : k.paidCashCents === 0
              ? 'No awards approved yet'
              : 'Nothing waiting to be paid',
      },
      budgetStat,
    ],
  };
}

export function SpendPage(): ReactElement {
  const [view, setView] = useView(spendView, spendSearch);
  const { data: spend } = useSuspenseQuery(spendQuery(view));
  const library = useLibrary();
  const { data: me } = useSuspenseQuery(meQuery);
  const span = yearSpan(spend.fiscalYear, spend.asOf);
  const hero = spendHero(spend, view, me);
  return (
    <>
      <Hero
        eyebrow={`Insights · ${sectorsOnly(spend.scope) ? 'budget & spend' : 'spend'} · ${scopeWords(spend.scope)} · ${span}`}
        // NAV1 (A47): a reader of their own sectors only knows this page as Budget & spend.
        title={sectorsOnly(spend.scope) ? 'Budget & spend' : 'Spend'}
        sub={hero.sub}
        {...(canOpen(library, 'budgets_history') && {
          actions: [{ label: 'Budgets & history', href: '/insights/budgets' }] as const,
        })}
        stats={hero.stats}
        titleTestId="insights-title"
      />
      <InsightsTabs />
      <PageBody>
        <SpendDashboard spend={spend} view={view} setView={setView} />
      </PageBody>
    </>
  );
}
